import swal from 'sweetalert2';
import { getAuthState } from '../../assets/scripts/login-util';

/**
 * "That needs a different plan" (Phase 8).
 *
 * A function, not a component, because it is called from a catch block -
 * mounting a component from inside an error handler means threading state
 * through every screen that might hit one.
 *
 *     try { ... } catch (err) {
 *         if (isEntitlementError(err)) return showUpgradePrompt('SUB_AGENCY');
 *         ...normal error handling
 *     }
 *
 * ==================== WHY IT MATCHES ON STRINGS ====================
 *
 * The server's 403 messages are a wire protocol this codebase has always
 * used - axiosFunctions.js has matched on them since long before billing
 * existed. 'Minimum 3 license' does not describe anything true (there is
 * no minimum and no 3), but it is what the server sends, so it is what we
 * match. Phase 7 kept those strings byte-identical for exactly this
 * reason; FEATURE_NOT_ENTITLED is accepted alongside them for when the
 * server starts sending it.
 */

const ENTITLEMENT_MESSAGES = [
    'Minimum 3 license',
    'FEATURE_NOT_ENTITLED',
    // Task 16: quotaRepository.js's QUOTA_ERRORS.POLICY_QUOTA_EXCEEDED
    // publicMessage (creating past the effective limit) and
    // policyRepository.js's POLICY_ACCESS_RESTRICTED publicMessage
    // (viewing/editing/deleting a policy outside the effective window) -
    // both read the same as a boolean feature refusal from here on ("you
    // need a different plan to do this"), so both reuse the same upgrade
    // prompt rather than a second, parallel error-matching mechanism.
    "reached your plan's policy limit",
    "outside your plan's current limit",
];

export const FEATURE_COPY = {
    SUB_AGENCY  : { name: 'Sub-agency',    blurb: 'Manage sub-agents and their policies.' },
    ANALYTICS   : { name: 'Analytics',     blurb: 'Deeper reporting across your book.' },
    BATCH_UPLOAD: { name: 'Batch upload',  blurb: 'Import policies and clients in bulk.' },
    API         : { name: 'API access',    blurb: 'Connect InsureBox to your other systems.' },
    SSO         : { name: 'Single sign-on', blurb: 'Sign in with your company identity provider.' },
    TEAM_ACCESS : { name: 'Team',  blurb: 'Let colleagues use InsureBox alongside you.' },
    POLICIES    : { name: 'More policies', blurb: "You've reached your plan's policy limit." },
};

export const isEntitlementError = (err) =>
    !!err && ENTITLEMENT_MESSAGES.some(m => String(err.message || '').includes(m));

/**
 * @param {String} featureCode  one of FEATURE_COPY, or omitted for a generic prompt
 * @param {Function} [navigate] history.push, when the caller has a router
 */
export const showUpgradePrompt = (featureCode, navigate) => {
    const copy    = FEATURE_COPY[featureCode];
    const authUser = getAuthState('user');
    const user     = (authUser && typeof authUser === 'object') ? authUser : {};
    const isOwner = Number(user.categoryId) === 1;

    return swal.fire({
        icon              : 'info',
        titleText         : copy ? `${copy.name} is not on your plan` : 'Not included in your plan',
        html              : copy
            ? `<p>${copy.blurb}</p><p class="kt-font-sm">It is available on a higher plan.</p>`
            : '<p>That feature is available on a higher plan.</p>',
        showCancelButton  : isOwner,
        buttonsStyling    : false,
        confirmButtonClass: 'btn btn-brand',
        cancelButtonClass : 'btn btn-secondary',
        // Only the account owner can change the plan, so only the owner is
        // offered the route to it. Sending a team member to a billing page
        // that refuses them is a dead end.
        confirmButtonText : isOwner ? 'See plans' : 'Close',
        cancelButtonText  : 'Not now',
    }).then((result) => {
        if (result.value && isOwner) {
            if (navigate) navigate('/billing/plans');
            else window.location.href = '/billing/plans';
        }
    });
};

/**
 * Drop-in replacement for the generic `catch (err) { swal.fire({icon:'error',...}) }`
 * block every screen already has. Routes an entitlement refusal to the
 * proper upgrade prompt; anything else falls through to the same plain
 * error modal as before, byte-identical to what these catch blocks
 * already did.
 *
 * ==================== WHY THIS EXISTS ====================
 *
 * `checkAuth.js`'s SUB_AGENCY gate (`isLicensedUrl` matching 'subAgency'/
 * 'team'/'subAgent' in the URL) covers every request the Team and Sub
 * Agency pages make, not just one button - so a tenant not entitled to
 * SUB_AGENCY sees 'Minimum 3 license' from the very first fetch on page
 * load (teams.js/subAgency.js's own visited-intro check), not from some
 * one obvious "upgrade-gated" action. `showUpgradePrompt` already existed
 * for exactly this message, but nothing on these two pages called it -
 * every catch block there just dumped the raw server string
 * ("Minimum 3 license", which reads like a made-up validation rule, not
 * a plan limit) into a generic error modal instead. This is the fix,
 * factored so every one of those catch blocks becomes a one-line swap.
 *
 * @param {Error} err
 * @param {String} [featureCode='SUB_AGENCY'] passed to showUpgradePrompt when err is an entitlement refusal
 * @param {Function} [navigate] history.push, when the caller has a router
 */
export const showErrorOrUpgradePrompt = (err, featureCode = 'SUB_AGENCY', navigate) => {
    if (isEntitlementError(err)) return showUpgradePrompt(featureCode, navigate);
    return swal.fire({
        icon              : 'error',
        titleText         : 'Error!',
        text              : err.message,
        buttonsStyling    : false,
        confirmButtonClass: 'btn btn-brand',
    });
};

/**
 * Click-guard for a tab/NavLink whose destination requires a feature the
 * tenant might not have (e.g. the Sub Agency page's Pay Out Plan/Sub
 * Agents/Report tabs, all genuinely SUB_AGENCY-gated server-side).
 *
 * ==================== WHY A GUARD, NOT JUST A CATCH ====================
 *
 * `showErrorOrUpgradePrompt` above reacts AFTER a gated request has
 * already failed - fine for a page's own data fetch, but wrong for a tab
 * switch: by the time that request 403s, react-router has already
 * navigated and the destination tab is already showing (empty, broken,
 * or flashing an error) before the modal even appears. This guard runs
 * ON THE CLICK, before any navigation happens, so an ungranted tenant
 * never sees the tab switch at all - only the same upgrade prompt every
 * other entitlement refusal in this app already uses.
 *
 * `granted` is passed in, not looked up here, on purpose: the caller
 * already knows this (one `hasFeature()` call made once on mount - see
 * subAgency.js), so the guard itself stays a synchronous, side-effect-
 * free-until-called function that's trivial to unit test without mocking
 * a network call.
 *
 * @param {Event} e             the click event - preventDefault() is called on it when blocked
 * @param {Boolean} granted     whether the tenant already has the feature
 * @param {String} featureCode  one of FEATURE_COPY
 * @param {Function} [navigate] history.push, when the caller has a router
 * @returns {Boolean} true if navigation was allowed to proceed, false if it was blocked
 */
export const guardFeatureNavigation = (e, granted, featureCode, navigate) => {
    if (granted) return true;
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    showUpgradePrompt(featureCode, navigate);
    return false;
};

export default showUpgradePrompt;
